// MultiCdmBreakScreen.js
// -----------------------------------------------------------------------------
// The screen between cases. Shows every case in the block with a decorative
// completion tick, marks which one is next, and offers Continue.
//
// It's also where a block begins: right after a block is created, all its cases
// are pending and case 1 is next, so this screen doubles as the "ready?" screen.
//
// Grades are deliberately absent — you don't see how you did until the whole
// block is finished.
//
// The countdown (only when the session enforces breaks):
//   - ticks down once a second and reports every tick up via onTick, so the
//     remaining seconds survive the user closing the app mid-break
//   - reaching zero starts the next case on its own
//   - if the user left and came back, MultiCdmApp hands us the stored remainder
//
// Props:
//   session       normalized session                              (required)
//   cases         merged case rows (for titles/categories)        (required)
//   nextEntry     the CaseProgress entry that runs next           (required)
//   starting      bool — start/resume call in flight              (required)
//   startError    string | null                                   (optional)
//   onContinue    () => void                                      (required)
//   onTick        (remainingSeconds) => void                      (required)
//   onSettings    (nextSettings) => void                          (required)
//   onExit        () => void — back to the session list           (required)
//   savingSettings bool                                           (optional)
// -----------------------------------------------------------------------------

import React from "react";
import MultiCdmSettingsPanel from "./MultiCdmSettingsPanel";
import {
  CASE_STATUS,
  caseTimingLabel,
  completedCount,
  formatElapsed,
} from "./multiCdmFetches";

function formatClock(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return mins + ":" + (secs < 10 ? "0" + secs : secs);
}

class MultiCdmBreakScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      remaining: this.initialRemaining(props),
      showSettings: false,
    };
    this.timer = null;
  }

  // Where the countdown starts. A stored remainder wins (the user was already
  // on a break and left), otherwise it's a fresh full break.
  initialRemaining(props) {
    const settings = props.session.CaseProgress.Settings;
    if (!settings.EnforceBreak) return null;

    const stored = props.session.CaseProgress.BreakRemainingSeconds;
    if (typeof stored === "number" && stored > 0) return stored;
    return settings.BreakMinutes * 60;
  }

  componentDidMount() {
    this.startTimer();
  }

  componentDidUpdate(prevProps) {
    const wasEnforced =
      prevProps.session.CaseProgress.Settings.EnforceBreak;
    const isEnforced = this.props.session.CaseProgress.Settings.EnforceBreak;
    const prevMinutes = prevProps.session.CaseProgress.Settings.BreakMinutes;
    const nextMinutes = this.props.session.CaseProgress.Settings.BreakMinutes;

    // Turning the break timer on mid-break, or changing its length, restarts
    // the countdown from the new value.
    if (!wasEnforced && isEnforced) {
      this.setState({ remaining: nextMinutes * 60 }, this.startTimer);
    } else if (wasEnforced && !isEnforced) {
      this.stopTimer();
      this.setState({ remaining: null });
    } else if (isEnforced && prevMinutes !== nextMinutes) {
      this.setState({ remaining: nextMinutes * 60 });
    }
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  startTimer() {
    this.stopTimer();
    if (!this.props.session.CaseProgress.Settings.EnforceBreak) return;
    if (this.state.remaining === null) return;
    this.timer = setInterval(this.tick, 1000);
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  tick = () => {
    this.setState(
      function (prevState) {
        if (prevState.remaining === null) return null;
        return { remaining: Math.max(0, prevState.remaining - 1) };
      },
      () => {
        const remaining = this.state.remaining;
        if (remaining === null) return;

        // Push the remainder up so it can be persisted. MultiCdmApp throttles
        // the actual network write.
        this.props.onTick(remaining);

        if (remaining <= 0) {
          this.stopTimer();
          // Don't fire while a start is already in flight.
          if (!this.props.starting) {
            this.props.onContinue();
          }
        }
      },
    );
  };

  caseRow(caseId) {
    const all = this.props.cases;
    for (let i = 0; i < all.length; i++) {
      if (all[i].ID === caseId) return all[i];
    }
    return null;
  }

  renderCaseRow(entry, index) {
    const { nextEntry, session } = this.props;
    const settings = session.CaseProgress.Settings;
    const row = this.caseRow(entry.CaseId);
    const isNext = nextEntry && nextEntry.CaseId === entry.CaseId;
    const done = entry.Status === CASE_STATUS.COMPLETED;
    const partial = entry.Status === CASE_STATUS.IN_PROGRESS;

    const meta = [];
    if (row && !settings.HideCaseCategory) meta.push(row.CASECATEGORYDESC);
    if (row) meta.push(caseTimingLabel(settings));
    if (done && typeof entry.ElapsedSeconds === "number") {
      meta.push("Took " + formatElapsed(entry.ElapsedSeconds));
    }

    // With titles hidden the case number carries the heading, so the row still
    // has something to lead with.
    const heading =
      row && !settings.HideCaseTitle ? row.TITLE : "Case " + entry.CaseId;
    const showSubline = !(row && settings.HideCaseTitle);

    let className = "mcdm-caserow";
    if (isNext) className += " mcdm-caserow--next";
    else if (!done) className += " mcdm-caserow--pending";

    return (
      <div className={className} key={entry.CaseId}>
        <span
          className={"mcdm-tick" + (done ? " mcdm-tick--done" : "")}
          role="img"
          aria-label={done ? "Completed" : "Not completed"}
        >
          {done ? "\u2713" : ""}
        </span>
        <span className="mcdm-caserow__num">{index + 1}</span>
        <div className="mcdm-caserow__body">
          <p className="mcdm-caserow__title">{heading}</p>
          <p className="mcdm-caserow__meta">
            {showSubline ? "Case " + entry.CaseId : ""}
            {showSubline && meta.length > 0 ? " \u00b7 " : ""}
            {meta.join(" \u00b7 ")}
          </p>
        </div>
        {isNext && (
          <span className="mcdm-tag mcdm-tag--next">
            {partial ? "Resume" : "Up next"}
          </span>
        )}
        {!isNext && partial && (
          <span className="mcdm-tag mcdm-tag--resume">Started</span>
        )}
      </div>
    );
  }

  renderCountdown() {
    const { remaining } = this.state;
    if (remaining === null) return null;

    const soon = remaining <= 30;
    return (
      <div
        className={"mcdm-countdown" + (soon ? " mcdm-countdown--soon" : "")}
        role="timer"
        aria-live="off"
      >
        <span className="mcdm-countdown__value">{formatClock(remaining)}</span>
        <span className="mcdm-countdown__label">
          Until the next case starts
        </span>
      </div>
    );
  }

  render() {
    const {
      session,
      nextEntry,
      starting,
      startError,
      onContinue,
      onExit,
      savingSettings,
    } = this.props;

    const progress = session.CaseProgress;
    const done = completedCount(progress);
    const total = progress.Cases.length;
    const started = done > 0;
    const resuming =
      nextEntry && nextEntry.Status === CASE_STATUS.IN_PROGRESS;

    let heading;
    let sub;
    if (!started && !resuming) {
      heading = "Ready when you are";
      sub =
        "You'll run these " +
        total +
        " cases back to back. Grades stay hidden until the block is done.";
    } else {
      heading = "Case " + done + " of " + total + " done";
      sub = resuming
        ? "You left this case partway through. Continue picks up where you stopped."
        : "Take a breather. Continue when you're ready for the next one.";
    }

    return (
      <div className="mcdm-scroll">
        <div className="mcdm-break">
          <div className="mcdm-break__head">
            <p className="mcdm-break__eyebrow">{session.Title}</p>
            <h1 className="mcdm-break__title">{heading}</h1>
            <p className="mcdm-break__sub">{sub}</p>
          </div>

          {this.renderCountdown()}

          <div className="mcdm-caselist">
            {progress.Cases.map((entry, index) => {
              return this.renderCaseRow(entry, index);
            })}
          </div>

          {startError && <p className="mcdm-inline-error">{startError}</p>}

          <div className="mcdm-break__actions">
            <button type="button" className="mcdm-btn" onClick={onExit}>
              Finish later
            </button>
            <button
              type="button"
              className="mcdm-btn"
              onClick={() => {
                this.setState({ showSettings: true });
              }}
            >
              Settings
            </button>
            <button
              type="button"
              className="mcdm-btn mcdm-btn--primary"
              onClick={onContinue}
              disabled={starting || !nextEntry}
            >
              {starting
                ? "Loading case..."
                : resuming
                  ? "Continue case " + nextEntry.CaseId
                  : nextEntry
                    ? "Start case " + nextEntry.CaseId
                    : "Block complete"}
            </button>
          </div>
        </div>

        {this.state.showSettings && (
          <div
            className="mcdm-modal-backdrop"
            onClick={() => {
              this.setState({ showSettings: false });
            }}
          >
            <div
              className="mcdm-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Block settings"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <h2 className="mcdm-modal__title">Block settings</h2>
              <p className="mcdm-modal__sub">
                These apply to the rest of this block, starting with the next
                case.
              </p>
              <MultiCdmSettingsPanel
                settings={session.CaseProgress.Settings}
                disabled={savingSettings}
                onChange={this.props.onSettings}
              />
              <div className="mcdm-modal__actions">
                <button
                  type="button"
                  className="mcdm-btn mcdm-btn--primary"
                  onClick={() => {
                    this.setState({ showSettings: false });
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default MultiCdmBreakScreen;